import React, { useState } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { ArrowLeft, ArrowRight, Check, ChevronDown, ChevronUp, Clock, Target, TrendingUp } from 'lucide-react';
import { RecoveryPlan, ProgressSnapshot, BaselineMetrics } from '../RecoveryMonitoring';

interface BaselineComparisonProps {
  recoveryPlan: RecoveryPlan;
  selectedSnapshotId: string;
  onSnapshotChange: (snapshotId: string) => void;
}

// Helper function to format dates
const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Color palette for the charts
const colors = {
  current: '#3b82f6', // blue
  target: '#10b981',  // green
  baseline: '#8b5cf6', // purple
  deficit: '#ef4444'  // red
};

const BaselineComparison: React.FC<BaselineComparisonProps> = ({ 
  recoveryPlan, 
  selectedSnapshotId, 
  onSnapshotChange 
}) => {
  const [showAllMetrics, setShowAllMetrics] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<'mobility' | 'strength' | 'balance'>('mobility');
  
  // Find the selected snapshot
  const selectedSnapshot = recoveryPlan.progressSnapshots.find(
    snapshot => snapshot.id === selectedSnapshotId
  ) || recoveryPlan.progressSnapshots[recoveryPlan.progressSnapshots.length - 1];
  
  // Find index of selected snapshot
  const selectedSnapshotIndex = recoveryPlan.progressSnapshots.findIndex(
    snapshot => snapshot.id === selectedSnapshotId
  );
  
  // Get previous and next snapshot IDs
  const previousSnapshotId = selectedSnapshotIndex > 0 
    ? recoveryPlan.progressSnapshots[selectedSnapshotIndex - 1].id 
    : null;
  
  const nextSnapshotId = selectedSnapshotIndex < recoveryPlan.progressSnapshots.length - 1 
    ? recoveryPlan.progressSnapshots[selectedSnapshotIndex + 1].id 
    : null;
  
  // Format time since recovery started
  const getWeeksSinceStart = (date: Date): string => {
    const diffTime = Math.abs(date.getTime() - recoveryPlan.recoveryStart.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);
    const remainingDays = diffDays % 7;
    
    return `Week ${diffWeeks}${remainingDays > 0 ? `, Day ${remainingDays}` : ''}`;
  };
  
  // Group metrics by category
  const metricsByCategory = {
    mobility: ['velocity', 'strideLength', 'stepTime', 'cadence'],
    strength: ['weightBearing', 'propulsionForce'],
    balance: ['symmetryIndex', 'balanceScore', 'rangeOfMotion']
  };
  
  // Determine metrics to display based on selected category and showAllMetrics flag
  const metricsToDisplay = showAllMetrics 
    ? Object.keys(selectedSnapshot.metrics)
    : metricsByCategory[selectedCategory] || [];
  
  // Calculate percentage of return to baseline
  const calculateBaselinePercentage = (metric: string, value: number): number => {
    if (!recoveryPlan.baselineMetrics) return 0;
    
    const baselineValue = recoveryPlan.baselineMetrics[metric as keyof BaselineMetrics];
    if (!baselineValue) return 0;
    
    // If the current value is higher than baseline, cap at 100%
    return Math.min(100, Math.round((value / baselineValue) * 100));
  };
  
  // Sort metrics by percentage of return to baseline
  const sortedMetrics = metricsToDisplay
    .map(metric => ({
      name: metric,
      value: selectedSnapshot.metrics[metric],
      percentage: calculateBaselinePercentage(metric, selectedSnapshot.metrics[metric]),
      fullName: metric
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
    }))
    .sort((a, b) => b.percentage - a.percentage); // Sort from highest to lowest
  
  // Prepare data for comparison bar chart
  const prepareComparisonData = () => {
    return sortedMetrics.map(metric => {
      const baselineValue = recoveryPlan.baselineMetrics 
        ? recoveryPlan.baselineMetrics[metric.name as keyof BaselineMetrics] 
        : 0;
      
      const targetValue = recoveryPlan.phases[recoveryPlan.currentPhase].targetMetrics[metric.name];
      
      return {
        name: metric.fullName,
        baseline: baselineValue,
        current: metric.value,
        target: targetValue
      };
    });
  };
  
  const comparisonData = prepareComparisonData();
  
  // Calculate overall recovery status
  const calculateOverallRecovery = (): number => {
    if (!recoveryPlan.baselineMetrics) return 0;
    
    const metrics = Object.keys(selectedSnapshot.metrics);
    let totalPercentage = 0;
    
    metrics.forEach(metric => {
      if (metric in recoveryPlan.baselineMetrics!) {
        totalPercentage += calculateBaselinePercentage(metric, selectedSnapshot.metrics[metric]);
      }
    });
    
    return Math.round(totalPercentage / metrics.length);
  };
  
  const overallRecoveryPercentage = calculateOverallRecovery();
  
  // Prepare data for progress over time chart
  const prepareProgressOverTimeData = () => {
    // Calculate average percentage return to baseline for each snapshot
    return recoveryPlan.progressSnapshots.map(snapshot => {
      const metrics = Object.keys(snapshot.metrics);
      let totalPercentage = 0;
      
      metrics.forEach(metric => {
        if (recoveryPlan.baselineMetrics && metric in recoveryPlan.baselineMetrics) {
          totalPercentage += calculateBaselinePercentage(metric, snapshot.metrics[metric]);
        }
      });
      
      const avgPercentage = Math.round(totalPercentage / metrics.length);
      
      return {
        date: formatDate(snapshot.date),
        timestamp: snapshot.date.getTime(),
        percentage: avgPercentage,
        week: getWeeksSinceStart(snapshot.date),
        isSelected: snapshot.id === selectedSnapshotId
      };
    }).sort((a, b) => a.timestamp - b.timestamp);
  };
  
  const progressOverTimeData = prepareProgressOverTimeData();
  
  // Prepare data for recovery gap visualization
  const prepareRecoveryGapData = () => {
    const gaps = sortedMetrics
      .filter(metric => metric.percentage < 100)
      .map(metric => ({
        name: metric.fullName,
        value: 100 - metric.percentage,
        fill: determineColor(metric.percentage)
      }))
      .slice(0, 5); // Show top 5 gaps
    
    return gaps;
  };
  
  const gapData = prepareRecoveryGapData();
  
  // Determine color based on recovery percentage
  function determineColor(percentage: number): string {
    if (percentage >= 85) return '#10b981'; // green
    if (percentage >= 70) return '#6366f1'; // indigo
    if (percentage >= 50) return '#f59e0b'; // amber
    return '#ef4444'; // red
  }
  
  return (
    <div className="p-6">
      {/* Selected Snapshot Navigation */}
      <div className="flex justify-between items-center mb-6 bg-gray-50 rounded-lg p-4">
        <button
          onClick={() => previousSnapshotId && onSnapshotChange(previousSnapshotId)}
          disabled={!previousSnapshotId}
          className={`p-2 rounded ${!previousSnapshotId ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-50'}`}
        >
          <ArrowLeft size={20} />
        </button>
        
        <div className="text-center">
          <h3 className="font-medium">
            {getWeeksSinceStart(selectedSnapshot.date)}
          </h3>
          <p className="text-sm text-gray-600">{formatDate(selectedSnapshot.date)}</p>
        </div>
        
        <button
          onClick={() => nextSnapshotId && onSnapshotChange(nextSnapshotId)}
          disabled={!nextSnapshotId}
          className={`p-2 rounded ${!nextSnapshotId ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-50'}`}
        >
          <ArrowRight size={20} />
        </button>
      </div>
      
      {/* Overall Recovery Status */}
      <div className="bg-white rounded-lg border p-6 mb-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="text-center md:text-left">
            <h3 className="text-lg font-medium mb-2 flex items-center justify-center md:justify-start">
              <Target className="mr-2 text-blue-500" />
              Overall Recovery Status
            </h3>
            <p className="text-sm text-gray-600 mb-4 max-w-md">
              Comparing current metrics to pre-injury baseline values. Higher percentages indicate closer proximity to baseline functionality.
            </p>
          </div>
          
          <div className="flex items-center">
            <div className="relative h-36 w-36">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <span className="text-3xl font-bold text-blue-600">{overallRecoveryPercentage}%</span>
                  <p className="text-sm text-gray-500">of baseline</p>
                </div>
              </div>
              <svg className="h-full w-full" viewBox="0 0 100 100">
                {/* Background circle */}
                <circle 
                  cx="50" 
                  cy="50" 
                  r="45" 
                  fill="none" 
                  stroke="#e5e7eb" 
                  strokeWidth="10" 
                />
                {/* Progress circle */}
                <circle 
                  cx="50" 
                  cy="50" 
                  r="45"
                  fill="none" 
                  stroke={determineColor(overallRecoveryPercentage)}
                  strokeWidth="10" 
                  strokeDasharray={`${2 * Math.PI * 45 * overallRecoveryPercentage / 100} ${2 * Math.PI * 45 * (100 - overallRecoveryPercentage) / 100}`}
                  transform="rotate(-90 50 50)"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
      
      {/* Progress Over Time */}
      <div className="bg-white rounded-lg border mb-6">
        <div className="p-4 border-b">
          <h3 className="font-medium flex items-center">
            <TrendingUp className="mr-2 text-indigo-500" />
            Recovery Progress Over Time
          </h3>
        </div>
        <div className="p-4">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={progressOverTimeData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis domain={[0, 100]} />
                <Tooltip
                  formatter={(value) => [`${value}% of baseline`, 'Recovery Status']}
                  labelFormatter={(label) => {
                    const item = progressOverTimeData.find(item => item.date === label);
                    return item ? `${item.week} - ${label}` : label;
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="percentage"
                  name="Recovery Status"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{
                    r: 4,
                    fill: "#3b82f6",
                    strokeWidth: 2,
                    stroke: "white"
                  }}
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-sm text-gray-600 mt-4">
            This chart shows your average recovery progress as a percentage of your pre-injury baseline metrics over time.
          </p>
        </div>
      </div>
      
      {/* Metric Category Selector */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-medium">Metric Comparison</h3>
        <div className="flex space-x-2">
          <button
            onClick={() => setSelectedCategory('mobility')}
            className={`px-3 py-1 text-sm rounded ${
              selectedCategory === 'mobility' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
            }`}
          >
            Mobility
          </button>
          <button
            onClick={() => setSelectedCategory('strength')}
            className={`px-3 py-1 text-sm rounded ${
              selectedCategory === 'strength' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
            }`}
          >
            Strength
          </button>
          <button
            onClick={() => setSelectedCategory('balance')}
            className={`px-3 py-1 text-sm rounded ${
              selectedCategory === 'balance' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
            }`}
          >
            Balance
          </button>
          <button
            onClick={() => setShowAllMetrics(!showAllMetrics)}
            className={`px-3 py-1 text-sm rounded ${
              showAllMetrics ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
            }`}
          >
            {showAllMetrics ? 'By Category' : 'All Metrics'}
          </button>
        </div>
      </div>
      
      {/* Detailed Metrics Comparison */}
      <div className="grid grid-cols-1 gap-6 mb-6">
        {/* Bar Chart Comparison */}
        <div className="bg-white rounded-lg border">
          <div className="p-4 border-b">
            <h3 className="font-medium">Current vs. Baseline Comparison</h3>
          </div>
          <div className="p-4">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={comparisonData}
                  layout="vertical"
                  margin={{ top: 20, right: 30, left: 100, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="name" width={100} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="baseline" name="Baseline (Pre-injury)" fill={colors.baseline} />
                  <Bar dataKey="current" name="Current" fill={colors.current} />
                  <Bar dataKey="target" name="Current Phase Target" fill={colors.target} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        
        {/* Percentage Cards */}
        <div className="bg-white rounded-lg border">
          <div className="p-4 border-b">
            <h3 className="font-medium">Percentage Return to Baseline</h3>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedMetrics.map(metric => (
              <div key={metric.name} className="p-3 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium">{metric.fullName}</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    metric.percentage >= 85 ? 'bg-green-100 text-green-800' :
                    metric.percentage >= 70 ? 'bg-blue-100 text-blue-800' :
                    metric.percentage >= 50 ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {metric.percentage}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5 mb-1">
                  <div 
                    className={`h-1.5 rounded-full ${
                      metric.percentage >= 85 ? 'bg-green-500' :
                      metric.percentage >= 70 ? 'bg-blue-500' :
                      metric.percentage >= 50 ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`}
                    style={{ width: `${Math.min(100, metric.percentage)}%` }}
                  ></div>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-gray-500">Current: {metric.value.toFixed(1)}</span>
                  <span className="text-xs text-gray-500">
                    Baseline: {recoveryPlan.baselineMetrics ? 
                      recoveryPlan.baselineMetrics[metric.name as keyof BaselineMetrics]?.toFixed(1) : 'N/A'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Recovery Gap Analysis */}
      <div className="bg-white rounded-lg border mb-6">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="font-medium">Recovery Gap Analysis</h3>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium mb-3 text-gray-700">Top Recovery Gaps</h4>
              <div className="space-y-4">
                {gapData.map((gap, index) => (
                  <div key={index} className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium">{gap.name}</span>
                      <span className="text-xs font-medium bg-red-100 text-red-800 px-2 py-0.5 rounded">
                        {gap.value}% gap
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div 
                        className="h-1.5 rounded-full bg-red-500" 
                        style={{ width: `${gap.value}%` }}
                      ></div>
                    </div>
                    <p className="mt-2 text-xs text-gray-600">
                      {gap.value > 50 
                        ? 'Significant gap - focus on improvement' 
                        : gap.value > 25 
                        ? 'Moderate gap - continue recovery work' 
                        : 'Minor gap - approaching baseline'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="h-72 flex flex-col justify-center">
              {gapData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={gapData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {gapData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => [`${value}% gap`, '']}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center h-full">
                  <Check size={48} className="text-green-500 mb-4" />
                  <p className="text-lg font-medium text-green-800">No significant recovery gaps detected!</p>
                  <p className="text-sm text-gray-600 text-center mt-2">
                    All metrics are at or close to baseline values. Excellent recovery progress!
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Recovery Recommendations */}
      <div className="bg-white rounded-lg border">
        <div className="p-4 border-b">
          <h3 className="font-medium">Recovery Recommendations</h3>
        </div>
        <div className="p-4">
          <div className="space-y-4">
            {sortedMetrics.length > 0 && sortedMetrics[sortedMetrics.length - 1].percentage < 70 && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h4 className="font-medium text-yellow-800 mb-1">Focus Area: {sortedMetrics[sortedMetrics.length - 1].fullName}</h4>
                <p className="text-sm text-yellow-700">
                  This metric is at {sortedMetrics[sortedMetrics.length - 1].percentage}% of your baseline. Consider prioritizing exercises that target this aspect of your recovery.
                </p>
              </div>
            )}
            
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-1">Overall Progress</h4>
              <p className="text-sm text-blue-700">
                {overallRecoveryPercentage >= 85 
                  ? 'You are making excellent progress in your recovery. Continue with your current rehabilitation program to maintain and further improve your progress.' 
                  : overallRecoveryPercentage >= 70 
                  ? 'You are making good progress in your recovery. Consider focusing on the areas with larger gaps to baseline for a more balanced recovery.' 
                  : 'You are making steady progress, but still have significant recovery opportunities. Follow your healthcare provider\'s recommendations and stay consistent with your exercises.'}
              </p>
            </div>
            
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="font-medium text-green-800 mb-1">Next Steps</h4>
              <p className="text-sm text-green-700">
                {overallRecoveryPercentage >= 85 
                  ? 'Consider transitioning to maintenance exercises to sustain your recovered functionality.' 
                  : overallRecoveryPercentage >= 70 
                  ? 'Continue with your current rehabilitation program with added focus on areas showing larger gaps.' 
                  : 'Adhere to your structured rehabilitation program and monitor progress weekly.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BaselineComparison; 